/**
 * Appearance Settings Section
 * Color mode and font preferences
 */
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { setTheme, setFont } from '../../utils/theme';

const COLOR_MODES = [
  {
    value: 'dark',
    label: 'Dark',
    description: 'Dark theme with reduced eye strain',
  },
  {
    value: 'light',
    label: 'Light',
    description: 'Light theme for bright environments',
  },
  {
    value: 'auto',
    label: 'Auto',
    description: 'Follows your system preference',
  },
];

const FONT_OPTIONS = [
  { value: 'inter', label: 'Inter (Default)', description: 'Clean and modern sans-serif' },
  { value: 'system', label: 'System Font', description: 'Uses your operating system font' },
  { value: 'dyslexic', label: 'OpenDyslexic', description: 'Designed for improved readability' },
];

export default function AppearanceSettings() {
  const { user } = useAuthStore();
  const { updateSettings, setHasUnsavedChanges } = useSettingsStore();

  const [colorMode, setColorMode] = useState(user?.color_mode || 'dark');
  const [chatFont, setChatFont] = useState(user?.chat_font || 'inter');
  const [showTooltips, setShowTooltips] = useState(user?.show_tooltips !== false); // Default: true
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Detect changes
  useEffect(() => {
    const hasChanges =
      colorMode !== (user?.color_mode || 'dark') ||
      chatFont !== (user?.chat_font || 'inter') ||
      showTooltips !== (user?.show_tooltips !== false);

    setHasUnsavedChanges(hasChanges);
  }, [colorMode, chatFont, showTooltips, user, setHasUnsavedChanges]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await updateSettings({
        color_mode: colorMode as 'dark' | 'light' | 'auto',
        chat_font: chatFont,
        show_tooltips: showTooltips,
      });

      setSaveMessage('Appearance preferences updated successfully!');
      setTimeout(() => setSaveMessage(''), 3000);

      // Apply theme (already previewed on click, this ensures persistence)
      setTheme(colorMode);
    } catch (error: any) {
      setSaveMessage(error.message || 'Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">Appearance</h3>
        <p className="text-sm text-dark-text-muted">
          Customize how PARACLETE looks and feels.
        </p>
      </div>

      {/* Color Mode */}
      <div>
        <label className="block text-sm font-medium text-dark-text mb-3">
          Color Mode
        </label>
        <div className="grid grid-cols-3 gap-3">
          {COLOR_MODES.map((mode) => (
            <button
              key={mode.value}
              onClick={() => { setColorMode(mode.value); setTheme(mode.value); }}
              className={`
                p-4 rounded-xl border-2 transition-all text-left
                ${
                  colorMode === mode.value
                    ? 'border-accent-blue bg-accent-blue/10'
                    : 'border-dark-border/50 hover:border-dark-border'
                }
              `}
            >
              <div className="font-medium text-dark-text mb-1">{mode.label}</div>
              <div className="text-xs text-dark-text-muted">{mode.description}</div>

              {/* Preview dots */}
              <div className="mt-3 flex gap-1">
                {mode.value === 'dark' && (
                  <>
                    <div className="w-3 h-3 rounded-full bg-dark-bg border border-dark-border"></div>
                    <div className="w-3 h-3 rounded-full bg-dark-card border border-dark-border"></div>
                    <div className="w-3 h-3 rounded-full bg-dark-text"></div>
                  </>
                )}
                {mode.value === 'light' && (
                  <>
                    <div className="w-3 h-3 rounded-full bg-white border border-gray-300"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-100 border border-gray-300"></div>
                    <div className="w-3 h-3 rounded-full bg-gray-900"></div>
                  </>
                )}
                {mode.value === 'auto' && (
                  <>
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-dark-bg to-white border border-dark-border"></div>
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-dark-card to-gray-100 border border-dark-border"></div>
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-dark-text to-gray-900"></div>
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Font */}
      <div>
        <label className="block text-sm font-medium text-dark-text mb-3">
          Chat Font
        </label>
        <div className="space-y-2">
          {FONT_OPTIONS.map((font) => (
            <button
              key={font.value}
              onClick={() => { setChatFont(font.value); setFont(font.value); }}
              className={`
                w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between
                ${
                  chatFont === font.value
                    ? 'border-accent-blue bg-accent-blue/10'
                    : 'border-dark-border/50 hover:border-dark-border'
                }
              `}
            >
              <div>
                <div className="font-medium text-dark-text">{font.label}</div>
                <div className="text-xs text-dark-text-muted mt-1">{font.description}</div>
              </div>

              {chatFont === font.value && (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-accent-blue">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Show Tooltips Toggle */}
      <div>
        <label className="block text-sm font-medium text-dark-text mb-3">
          Button Tooltips
        </label>
        <button
          onClick={() => setShowTooltips(!showTooltips)}
          className={`
            w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between
            ${
              showTooltips
                ? 'border-accent-blue bg-accent-blue/10'
                : 'border-dark-border/50 hover:border-dark-border'
            }
          `}
        >
          <div>
            <div className="font-medium text-dark-text">
              {showTooltips ? 'Tooltips Enabled' : 'Tooltips Disabled'}
            </div>
            <div className="text-xs text-dark-text-muted mt-1">
              {showTooltips
                ? 'Hover or long-press buttons to see helpful descriptions'
                : 'Button descriptions hidden for cleaner interface'}
            </div>
          </div>

          {/* Toggle Switch */}
          <div className={`
            relative w-12 h-6 rounded-full transition-colors
            ${showTooltips ? 'bg-accent-blue' : 'bg-dark-border'}
          `}>
            <div className={`
              absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform
              ${showTooltips ? 'right-0.5' : 'left-0.5'}
            `} />
          </div>
        </button>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4 pt-4 border-t border-dark-border/50">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>

        {saveMessage && (
          <span className={`text-sm ${saveMessage.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
            {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
}
